<template>
  <v-card :class="['disparadores', $v.localChosenSenders.$error ? 'disparadores-error' : '']" height="363px">
    <mf-loading loader-type="dynamic" :dynamic-amount="2" :dynamic-width="100" :boolean-loader="$apollo.queries.senders.loading" fill-height>
      <v-container class="pa-0" fluid fill-height>
        <v-row no-gutters class="pr-3">
          <h3 class="table-card-title pl-4 pt-2">Disparar mensagens dos números</h3>
          <v-spacer />
          <mf-search v-model="search" dense outlined hide-details placeholder="Buscar disparador" />
        </v-row>
        <v-row v-if="$v.localChosenSenders.$error" no-gutters>
          <v-col class="pa-0 pl-4">
            <mf-icon class="disparadores-error-icon" small color="vermelho-l20"> info </mf-icon>
            <span class="disparadores-error-label pl-2 error--text">Selecione ao menos um número para enviar a campanha.</span>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col>
            <v-data-table
              v-model="localChosenSenders"
              height="205px"
              fixed-header
              hide-default-footer
              disable-pagination
              :headers="headers"
              :items="items"
              item-key="number"
              :show-select="!disabled"
            >
              <template v-slot:header.nof_contacts="{ header }">
                {{ header.text }}

                <v-tooltip top max-width="245">
                  <template v-slot:activator="{ on }">
                    <span v-on="on">
                      <mf-icon small color="cinza-0">info</mf-icon>
                    </span>
                  </template>
                  <span>Um contato é um número de whatsapp que se inscreveu nesse canal mandando mensagem para o número divulgado.</span>
                </v-tooltip>
              </template>

              <template v-slot:header.nof_community_contacts="{ header }">
                {{ header.text }}

                <v-tooltip top max-width="245">
                  <template v-slot:activator="{ on }">
                    <span v-on="on">
                      <mf-icon small color="cinza-0">info</mf-icon>
                    </span>
                  </template>
                  <span>Um contato na comunidade é um número de whatsapp que se inscreveu em alguma das comunidades atreladas a este disparador.</span>
                </v-tooltip>
              </template>
              <template v-slot:item.number="{ item }">
                {{ formatNumber(item.number) }}
              </template>
              <template v-slot:item.nof_contacts="{ item }">
                {{ item.nof_contacts | localeString(0) }}
              </template>
              <template v-slot:item.nof_community_contacts="{ item }">
                {{ item.nof_community_contacts | localeString(0) }}
              </template>
              <template v-slot:item.connection_status="{ item }">
                <mf-icon fill :color="statusColor(item.connection_status)">fiber_manual_record</mf-icon>
                {{ statusText(item.connection_status) }}
              </template>
            </v-data-table>
          </v-col>
        </v-row>
        <warning-row class="pl-4"> Apenas os consumidores que se cadastraram através dos números selecionados irão receber essa campanha </warning-row>
      </v-container>
    </mf-loading>
  </v-card>
</template>

<script>
import gql from 'graphql-tag'
import capitalize from 'lodash/capitalize'
import { phoneNumberFormat } from '@/helpers/phoneNumberFormat.js'
import { required, minLength } from 'vuelidate/lib/validators'
import { diacriticSpaceSensitiveRegex } from '@/helpers/search.js'
import whatsappStatusMixin from '@/modules/campanhas/whatsapp/mixins/whatsapp-status.js'

const QUERY_GET_NUMBERS = gql`
  query GetNumbers {
    getNumbers(type: SENDER) {
      _id
      name
      number
      connection_status
      nof_contacts
      nof_community_contacts
    }
  }
`

export default {
  name: 'Disparadores',
  components: {
    WarningRow: () => import('@/components/WarningRow.vue'),
  },
  mixins: [whatsappStatusMixin],
  props: {
    chosenSenders: Array,
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    localChosenSenders: [],
    search: '',
  }),
  computed: {
    headers() {
      return [
        {
          text: 'Nome',
          value: 'name',
        },
        {
          text: 'Número',
          value: 'number',
          width: '15%',
        },
        {
          text: 'Contatos',
          value: 'nof_contacts',
          width: '15%',
        },
        ...(this.isExternalProvider ? [{ text: 'Contatos na comunidade', value: 'nof_community_contacts' }] : []),
        {
          text: 'Status',
          value: 'connection_status',
        },
      ]
    },
    isExternalProvider() {
      return this.$getAllowedModules()['external_whatsapp_provider']
    },
  },
  apollo: {
    senders: {
      query: QUERY_GET_NUMBERS,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      update({ getNumbers }) {
        if (!getNumbers) return []
        if (!this.disabled) {
          if (this.chosenSenders.length) {
            this.localChosenSenders = getNumbers.filter((sender) =>
              this.chosenSenders.map((chosenSender) => (chosenSender?.number ? chosenSender?.number : chosenSender)).includes(sender.number)
            )
          } else {
            this.localChosenSenders = getNumbers
          }
        } else {
          if (this.chosenSenders && this.chosenSenders.length) {
            this.localChosenSenders = getNumbers.filter((sender) => {
              return this.chosenSenders.some((chosen) => chosen.number === sender.number) || this.chosenSenders.includes(sender.number)
            })
          } else {
            this.localChosenSenders = getNumbers
          }
        }
        return getNumbers
      },
    },
  },
  watch: {
    localChosenSenders(value) {
      if (this.localChosenSenders) {
        this.$emit(
          'update:chosenSenders',
          this.localChosenSenders.map((sender) => sender.number)
        )
      }
      this.validate()
    },
    search: {
      handler(value) {
        if (value) {
          const searchRegex = diacriticSpaceSensitiveRegex(value)
          this.items = this.senders.filter((sender) => {
            return sender.name.match(searchRegex) || sender.number.match(searchRegex)
          })
          return
        }
        this.items = this.senders
      },
      immediate: true,
    },
    senders() {
      this.items = this.senders
    },
  },
  methods: {
    capitalize(str) {
      return capitalize(str)
    },
    formatNumber(number) {
      return phoneNumberFormat(number)
    },
    validate() {
      this.$v.$reset()
      this.$v.$touch()
    },
  },
  validations() {
    return {
      localChosenSenders: {
        required,
        minLength: minLength(1),
      },
    }
  },
}
</script>

<style lang="scss">
.disparadores {
  color: $cinza-d10;
  font-size: 14px;
}
.disparadores-error {
  border-left: 5px solid $vermelho-0 !important;
  .disparadores-error-label {
    font-size: 12px;
    letter-spacing: 0.2px;
    color: $vermelho-0;
  }
}
</style>
